import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { ToastProvider } from './context/ToastContext';
import { GoogleOAuthProvider } from "@react-oauth/google";

// Import Pages
import Dashboard from "./pages/Dashboard";
import Login from "./pages/Login";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Scans from "./pages/Scans";
import SSLChecker from "./pages/SSLChecker";
import ServerDetails from "./pages/ServerDetails";
import Register from "./pages/Register";
import ScanCheck from "./pages/ScanCheck";
import DemoCheck from "./pages/DemoCheck";
import Home from './pages/Home';
import Plans from './pages/Plans';
import About from './pages/About';
import Roadmap from './pages/Roadmap';
import AppComingSoon from './pages/AppComingSoon';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Documentation from './pages/Documentation';
import ManagementLogin from './pages/ManagementLogin';
import ManagementDashboard from './pages/ManagementDashboard';
import ManagementUsers from './pages/ManagementUsers';
import ManagementLogs from './pages/ManagementLogs';
import ManagementSettings from './pages/ManagementSettings';
import ManagementCoupons from './pages/ManagementCoupons';
import ManagementFindings from './pages/ManagementFindings';
import ManagementDiscoveredAssets from './pages/ManagementDiscoveredAssets';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import MScanCheck from './pages/MScanCheck';
import Settings from './pages/Settings';
import DOS from './pages/DOS';
import BuildDetails from './pages/BuildDetails';
import Security from './pages/Security';
import LighthouseAudit from './pages/LighthouseAudit';
// import TrafficProtection from './pages/TrafficProtection';


import { isManagementSession, clearManagementSession } from "./utils/managementAuth";

// Route guards — defined outside render to prevent remounting on every re-render
const ProtectedRoute = ({ children }) => {
  const token = sessionStorage.getItem("authToken");
  return token ? children : <Navigate to="/home" replace />;
};

const AdminProtectedRoute = ({ children }) => {
  if (isManagementSession()) return children;
  clearManagementSession();
  return <Navigate to="/management" replace />;
};

const PublicRoute = ({ children }) => {
  const token = sessionStorage.getItem("authToken");
  return token ? <Navigate to="/" replace /> : children;
};

const AdminPublicRoute = ({ children }) => {
  return isManagementSession() ? <Navigate to="/management/dashboard" replace /> : children;
};

// ✅ App wrapper content
function AppContent() {
  const location = useLocation();

  return (
    <div className="d-flex flex-column min-vh-100">
      <Navbar />

      <main className="flex-grow-1">
        <Routes>
          {/* Public routes - only accessible when NOT logged in */}
          <Route
            path="/home"
            element={<Home />}
          />
          <Route
            path="/about"
            element={<About />}
          />
          <Route
            path="/plans"
            element={<Plans />}
          />
          <Route
            path="/roadmap"
            element={<Roadmap />}
          />
          <Route
            path="/app"
            element={<AppComingSoon />}
          />
          <Route
            path="/privacy"
            element={<PrivacyPolicy />}
          />
          <Route
            path="/terms"
            element={<TermsOfService />}
          />
          <Route
            path="/documentation"
            element={<Documentation />}
          />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotPassword />
              </PublicRoute>
            }
          />
          <Route
            path="/reset-password/:token"
            element={
              <PublicRoute>
                <ResetPassword />
              </PublicRoute>
            }
          />
          <Route
            path="/demo-check"
            element={
              <PublicRoute>
                <DemoCheck />
              </PublicRoute>
            }
          />

          {/* Management routes */}
          <Route
            path="/management"
            element={
              <AdminPublicRoute>
                <ManagementLogin />
              </AdminPublicRoute>
            }
          />
          <Route
            path="/management/dashboard"
            element={
              <AdminProtectedRoute>
                <ManagementDashboard />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/management/users"
            element={
              <AdminProtectedRoute>
                <ManagementUsers />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/management/logs"
            element={
              <AdminProtectedRoute>
                <ManagementLogs />
              </AdminProtectedRoute>
            }
          />


          <Route
            path="/management/settings"
            element={
              <AdminProtectedRoute>
                <ManagementSettings />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/management/coupons"
            element={
              <AdminProtectedRoute>
                <ManagementCoupons />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/management/findings"
            element={
              <AdminProtectedRoute>
                <ManagementFindings />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/management/discovered-assets"
            element={
              <AdminProtectedRoute>
                <ManagementDiscoveredAssets />
              </AdminProtectedRoute>
            }
          />

          <Route
            path="/management/login"
            element={<Navigate to="/management" replace />}
          />

          {/* Protected routes - only accessible when logged in */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route path="/scans" element={<ProtectedRoute><Scans /></ProtectedRoute>} />
          <Route path="/ssl-check/:domain" element={<ProtectedRoute><SSLChecker /></ProtectedRoute>} />
          <Route path="/scan-check/:domain" element={<ProtectedRoute><ScanCheck /></ProtectedRoute>} />
          <Route path="/mscan-check/:domain" element={<ProtectedRoute><MScanCheck /></ProtectedRoute>} />

          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/server-details/:domain" element={<ProtectedRoute><ServerDetails /></ProtectedRoute>} />
          <Route path="/build-details/:domain" element={<ProtectedRoute><BuildDetails /></ProtectedRoute>} />
          <Route path="/dos/:domain" element={<ProtectedRoute><DOS /></ProtectedRoute>} />
          <Route path="/security/:domainId" element={<ProtectedRoute><Security /></ProtectedRoute>} />
          <Route path="/lighthouse-audit/:domain" element={<ProtectedRoute><LighthouseAudit /></ProtectedRoute>} />

          {/* Fallback route */}
          <Route path="*" element={<Navigate to={sessionStorage.getItem("authToken") ? "/" : "/home"} replace />} />
        </Routes>
      </main>

      {!location.pathname.startsWith('/management') && <Footer />}
    </div>
  );
}

function App() {
  return (
    <Router>
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || "YOUR_GOOGLE_CLIENT_ID"}>
        <ToastProvider>
          <AppContent />
        </ToastProvider>
      </GoogleOAuthProvider>
    </Router>
  );
}

export default App;

// (End of file)